/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { makeStyles } from "@material-ui/core/styles";

export const useStyles = makeStyles((theme) => ({
    containerList: {
        '& .MuiPaper-root:first-child' : {
            backgroundColor: theme.custom.applicationViewPage.tabs.subscriptions.styles.backgroundColor,
            '& .MuiTable-root:first-child' : {
                '& .MuiTableBody-root' : {
                    backgroundColor: theme.custom.applicationViewPage.tabs.subscriptions.styles.backgroundColor,
                }
            }
        }
    },
    asterisk: {
        color: 'red',
    },
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignSelf: "center",
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    loaderContainer: {
        display: "flex",
        minHeight: theme.spacing(20),
        alignItems: "center",
        justifyContent: "center"
    },
    textLabel: {
        marginBottom: theme.spacing(1.5),
        fontFamily: 'GilmerMedium',
        fontSize: '13px',
        color: '#1D2028',
        lineHeight: '24px'
    },
    textLabelWithAsterisk: {
        marginBottom: theme.spacing(1.5),
        marginTop: theme.spacing(-0.5),
        fontFamily: 'GilmerMedium',
        fontSize: '13px',
        color: '#1D2028',
        lineHeight: '24px'
    },
    textInput: {
        width: "100%",
        '& .MuiInputBase-input': {
            fontSize: 14
        },
        '& .MuiOutlinedInput-root': {
            height: theme.spacing(6)
        },
        '& .MuiFormHelperText-contained': {
            marginRight: 0,
            minHeight: theme.spacing(3.75)
        }
    },
    multiLineInput: {
        width: "100%",
        '& .MuiInputBase-input': {
            fontSize: 14
        },
        '& .MuiFormHelperText-root.Mui-error': {
            background: "url('/images/warning-red.svg') no-repeat left top",
            margin: 0,
            paddingLeft: theme.spacing(3),
            marginTop: theme.spacing(1),
            color: "#EA4C4D"
        }
    },
    selectBox: {
        height: theme.spacing(6),
        width: "100%"
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[200]
    },
    createApiDialogContent: {
    },
    dialogTitle: {
        paddingTop: `${theme.spacing(1)}px !important`,
        paddingBottom: `${theme.spacing(0)}px !important`,
    },
    primaryTitle: {
        color: '#222228',
        textAlign: "center",
        fontFamily: 'GilmerBold',
        fontSize: '29px',
    },
    customWidth: {
        borderRadius: '12px',
        maxHeight: '70vw',
    },
    dialogBtn: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        fontSize: 13,
        fontFamily: 'GilmerMedium'
    },
    regenerateBtn: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        fontSize: 13,
        fontFamily: 'GilmerMedium',
        backgroundColor: '#f44336', 
        color: '#fff',
        "&:hover": {
            backgroundColor: "#FCEDED",
            color: "#EA4C4D",
            borderColor: "#EA4C4D",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)"
        },
        "&:focus, &:active": {
            borderColor: "#EA4C4D",
            backgroundColor: "#EA4C4D !important",
            color: theme.palette.common.white,
            backgroundRepeat: "no-repeat",
            backgroundSize: theme.spacing(2.5),
            backgroundPosition: "left",
            backgroundImage: "url('../../../themes/default/images/clear-white.svg')",
        }
    },
    cancelBtn: {
        marginRight: theme.spacing(1),
        height: theme.spacing(5),
        fontSize: 13,
        fontFamily: 'GilmerMedium'
    },
    dropdownStyle: {
        backgroundColor: "#fff",
        boxSizing: "border-box",
        width: "auto",
        border: "1px solid #DEE0E7",
        borderRadius: "5px",
        boxShadow: "0 5px 10px -3px rgba(50,50,77,0.1)",
        color: "#222228",
        marginTop: '0.25rem'
    },
    warningIcon: {
        height: theme.spacing(2.125),
        width: theme.spacing(2.125),
        marginRight: theme.spacing(1),
        marginLeft: -theme.spacing(1)
    },
    helperText: {
        display: "flex",
        alignItems: "center",
        fontSize: 10.5
    },
    textField: {
        width: theme.spacing(71.5),
        borderRadius: theme.spacing(0.625),
        backgroundColor: theme.palette.common.white,
        boxShadow: 'inset 0 0 0 1px #dee0e7, 0 1px 2px -1px rgba(0,0,0,0.08)',
        border: 1,
        '& .MuiOutlinedInput-input': {
          color: theme.palette.grey[500],
          paddingLeft: theme.spacing(1),
        },
        '& .MuiOutlinedInput-root': {
          height: 'auto',
        },
    },
    tag: {
        '& .MuiAutocomplete-tag': {
            backgroundColor: theme.palette.secondary.light,
            height: theme.spacing(3),
            marginBottom: theme.spacing(1),
            borderRadius: theme.spacing(0.625),
            position: "relative",
            zIndex: 0,
            border: "1px solid #979797",
            "& .MuiChip-label": {
              color: theme.palette.secondary.dark,
            },
            "& .MuiChip-deleteIcon": {
              color: theme.palette.secondary.main,
            },
        },
    },
    policyCardGridItem: {
        display: 'flex',
        flexDirection: 'row',
        marginTop: theme.spacing(2),
    },
    btnText: {
        textTransform: "initial",
        fontSize: 11,
        marginLeft: theme.spacing(1.625),
        fontWeight: 400
    },
    editBtn: {
        height: theme.spacing(3.5),
        backgroundSize: theme.spacing(2.5),
    },
    hoverBtn: {
        height: theme.spacing(3.5),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        color: theme.palette.primary.main,
        borderRadius: theme.spacing(3),
        border: "1px solid",
        borderColor: "#CBCFF1",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left",
        fontSize: 12,
        textTransform: "none",
        "&:hover": {
            backgroundColor: "rgb(204, 209, 242, 0.3)",
            color: theme.palette.primary.main,
            borderColor: "#A6B3FF",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)"
        },
        "&:focus": {
            backgroundColor: `${theme.palette.primary.main} !important`,
            color: theme.palette.common.white,
            borderColor: theme.palette.primary.main
        }
    },
    deleteBtn: {
        height: theme.spacing(3.5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(2),
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
        color: "#EA4C4D",
        borderRadius: theme.spacing(2),
        border: "1px solid",
        borderColor: "#F8C7C7",
        marginLeft: theme.spacing(1),
        backgroundRepeat: "no-repeat",
        backgroundSize: theme.spacing(2.5),
        backgroundPosition: "left",
        backgroundImage: "url('../../../../../themes/default/images/clear-red.svg')",
        textTransform: "none",
        fontSize: 12,
        "&:hover": {
            backgroundColor: "#FCEDED",
            color: "#EA4C4D",
            borderColor: "#EA4C4D",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)"
        },
        "&:focus, &:active": {
            borderColor: "#EA4C4D",
            backgroundColor: "#EA4C4D !important",
            color: theme.palette.common.white,
            backgroundRepeat: "no-repeat",
            backgroundSize: theme.spacing(2.5),
            backgroundPosition: "left",
            backgroundImage: "url('../../../themes/default/images/clear-white.svg')",
        }
    },
    confirmationText: {
        marginBottom: theme.spacing(1),
    },
    apiKeyName: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    hyperlinkNameColumn: {
        cursor: 'pointer',
        color: '#5567D5'
    },
    container: {
        paddingTop: theme.spacing(2.5),
        paddingBottom: theme.spacing(4),
        maxWidth: '1560px',
    },
    componentPadding: {
        paddingLeft: theme.spacing(7),
        paddingRight: theme.spacing(10),
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
        [theme.breakpoints.up('lg')]: {
            paddingLeft: 'auto',
            paddingRight: 'auto',
        }
    },
    
}));

export default useStyles;
